import React, { useMemo, useState } from 'react';
import { ITag } from '@app/common';
import { tagApi } from '@app/store';
import { extractData } from '@app/helpers/utils';
import { clickAway, colors, fontFamily, iconButton } from './editor.styles';
import { CloseIcon, InfoIcon, SearchIcon } from './icons';

export type TrackedTag = Pick<ITag, '_id' | 'name'>;

interface AddCategoryPopoverProps {
  trackedIds: string[];
  onAdd: (tag: TrackedTag) => void;
  onClose: () => void;
}

const normalize = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const AddCategoryPopover = ({ trackedIds, onAdd, onClose }: AddCategoryPopoverProps) => {
  const [search, setSearch] = useState('');

  // Les catégories sont communes à tous les catalogues de l'organisation : on charge tout et on filtre localement
  const { data, isFetching } = tagApi.useReadManyTagQuery({
    pagination: { take: 500, skip: 0 },
    sort: ['name:ASC'],
    filters: {},
  } as any);

  const tags = useMemo(() => extractData(data) as ITag[], [data]);

  const options = useMemo(() => {
    const byId = new Map(tags.map((tag) => [tag._id, tag]));
    const children = new Map<string | undefined, ITag[]>();

    tags.forEach((tag) => {
      const parentId = tag.parentTagId && byId.has(tag.parentTagId) ? tag.parentTagId : undefined;
      children.set(parentId, [...(children.get(parentId) || []), tag]);
    });

    const ancestors = (tag: ITag) => {
      const result: ITag[] = [];
      let current = tag.parentTagId ? byId.get(tag.parentTagId) : undefined;

      while (current && !result.includes(current)) {
        result.push(current);
        current = current.parentTagId ? byId.get(current.parentTagId) : undefined;
      }

      return result;
    };

    const ordered: { tag: ITag; depth: number }[] = [];
    const walk = (parentId: string | undefined, depth: number) => {
      [...(children.get(parentId) || [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((tag) => {
          ordered.push({ tag, depth });
          walk(tag._id, depth + 1);
        });
    };

    walk(undefined, 0);

    const query = normalize(search.trim());

    return ordered
      .map(({ tag, depth }) => {
        const parents = ancestors(tag);
        // Le back-end compte un produit dans ses catégories parentes : inutile de suivre une sous-catégorie déjà couverte
        const coveredBy = parents.find((parent) => trackedIds.includes(parent._id));
        const subCount = (children.get(tag._id) || []).length;

        return {
          tag,
          depth,
          haystack: normalize([tag.name, ...parents.map((parent) => parent.name)].join(' ')),
          isTracked: trackedIds.includes(tag._id),
          coveredBy,
          sub: parents.length
            ? `Dans ${parents[0].name}`
            : subCount
              ? `${subCount} sous-catégorie${subCount > 1 ? 's' : ''} incluse${subCount > 1 ? 's' : ''}`
              : '',
        };
      })
      .filter((option) => !query || option.haystack.includes(query));
  }, [tags, trackedIds, search]);

  return (
    <>
      <div style={clickAway} onClick={onClose} />

      <div
        role="dialog"
        aria-labelledby="capacity-add-category-title"
        onKeyDown={(event) => event.key === 'Escape' && onClose()}
        style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, zIndex: 10, width: 400, boxSizing: 'border-box', padding: 18, background: colors.surface, border: '1px solid rgba(253,255,252,0.14)', borderRadius: 14, boxShadow: '0 20px 50px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', gap: 14, color: colors.text, fontFamily }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h2 id="capacity-add-category-title" style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Limiter une catégorie</h2>
          <button type="button" aria-label="Fermer" onClick={onClose} style={{ ...iconButton, width: 36, height: 36 }}>
            <CloseIcon size={16} />
          </button>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, height: 44, boxSizing: 'border-box', padding: '0 12px', borderRadius: 9, border: `1px solid ${colors.borderStrong}`, background: colors.background, color: colors.muted }}>
          <SearchIcon />
          <input
            type="search"
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher une catégorie"
            aria-label="Rechercher une catégorie"
            style={{ flexGrow: 1, minWidth: 0, height: 40, border: 'none', outline: 'none', background: 'transparent', color: colors.text, fontFamily, fontSize: 14 }} />
        </label>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 320, overflowY: 'auto' }}>
          {options.map(({ tag, depth, isTracked, coveredBy, sub }) => {
            const isDisabled = isTracked || !!coveredBy;

            return (
              <button
                key={tag._id}
                type="button"
                disabled={isDisabled}
                onClick={() => onAdd({ _id: tag._id, name: tag.name })}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 52, boxSizing: 'border-box', padding: `8px 10px 8px ${10 + depth * 20}px`, borderRadius: 8, border: 'none', background: 'transparent', color: colors.text, fontFamily, textAlign: 'left', cursor: isDisabled ? 'default' : 'pointer', opacity: isDisabled ? 0.6 : 1 }}>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{tag.name}</span>
                  {!!sub && <span style={{ fontSize: 12, color: colors.muted }}>{sub}</span>}
                </span>

                <span style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: isDisabled ? colors.muted : colors.accent }}>
                  {isTracked ? 'Déjà limitée' : coveredBy ? `Comptée dans ${coveredBy.name}` : 'Ajouter'}
                </span>
              </button>
            );
          })}

          {!options.length && (
            <span style={{ padding: '14px 10px', fontSize: 13, color: colors.muted }}>
              {isFetching ? 'Chargement des catégories…' : 'Aucune catégorie ne correspond à cette recherche.'}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', paddingTop: 12, borderTop: `1px solid ${colors.border}`, fontSize: 12, lineHeight: 1.45, color: colors.muted }}>
          <span style={{ flexShrink: 0, marginTop: 2 }}><InfoIcon /></span>
          <span>Une catégorie parente compte aussi les produits de ses sous-catégories. Les catégories sont celles de l'organisation, communes à tous les catalogues.</span>
        </div>
      </div>
    </>
  );
};
